import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import { useApp } from '@/context/AppContext'
import { callCoach } from '@/utils/ai'
import MarkdownText from '@/components/common/MarkdownText'
import { PREFILL_KEY } from '@/utils/agentHandoff'
import VoiceAgent from '@/components/VoiceAgent'

// ── Quick-start prompts ────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  { label: '🏛️ Prepare for my hearing', text: 'Help me prepare for my upcoming custody hearing. What should I know and do before I go?' },
  { label: '👗 What to wear & how to act', text: 'What should I wear to court and how should I behave in the courtroom?' },
  { label: '📋 What to bring to court', text: 'What documents and evidence should I bring to my custody hearing?' },
  { label: '🗣️ What NOT to say', text: 'What are the most common mistakes people make when testifying in a custody case, and what should I avoid saying?' },
  { label: '👤 What is a GAL?', text: 'What is a Guardian Ad Litem and how can I work with them effectively?' },
  { label: '📜 Explain this order', text: 'Can you help me understand what a custody order typically says and what the terms mean?' },
]

// ── Message bubble ─────────────────────────────────────────────────────────────
function Bubble({ msg }) {
  const isUser = msg.role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-0.5">
          C
        </div>
      )}
      <div
        className={`max-w-[82%] px-4 py-3 rounded-2xl text-base leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-brand-700 text-white rounded-tr-sm'
            : 'bg-slate-100 text-slate-800 rounded-tl-sm'
        }`}
      >
        <MarkdownText content={msg.content} isUser={isUser} />
      </div>
    </div>
  )
}

// ── Typing indicator ───────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0">
        C
      </div>
      <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-tl-sm">
        <span className="flex gap-1 items-center h-4">
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </span>
      </div>
    </div>
  )
}

// ── Main screen ────────────────────────────────────────────────────────────────
export default function Coach() {
  const { user } = useApp()
  const location = useLocation()
  const [messages, setMessages] = useState([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const bottomRef               = useRef(null)
  const textareaRef             = useRef(null)

  // Pre-fill from agent handoff
  useEffect(() => {
    const prefill = location.state?.[PREFILL_KEY]
    if (prefill) {
      setInput(prefill)
      textareaRef.current?.focus()
      window.history.replaceState({}, '')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom whenever messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'
  }, [input])

  async function sendMessage(text) {
    const msg = text || input.trim()
    if (!msg || loading) return

    setInput('')
    setError('')

    const newMessages = [...messages, { role: 'user', content: msg }]
    setMessages(newMessages)
    setLoading(true)

    try {
      // Build history in the format the API expects
      const history = newMessages.map(m => ({ role: m.role, content: m.content }))

      const data = await callCoach({
        message: msg,
        history: history.slice(0, -1), // exclude the message we just sent (it's the current message)
        user: user ? { name: user.name, state: user.state, situation: user.situation } : undefined,
      })

      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }])
    } catch (err) {
      setError('Something went wrong. Please try again.')
      // Remove the user message if we failed
      setMessages(messages)
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const isEmpty = messages.length === 0

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Court Prep Coach" showBack />

      {/* ── Chat area ── */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4">

        {/* Welcome / empty state */}
        {isEmpty && (
          <div className="mb-6">
            <div className="bg-gradient-to-br from-brand-700 to-brand-500 rounded-2xl p-5 text-white mb-5">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl mb-3">🎓</div>
              <h2 className="text-lg font-bold mb-1">Your Personal Court Coach</h2>
              <p className="text-base text-white/85 leading-relaxed">
                I explain legal terms in plain language, help you prepare for hearings, and guide you through each step — so you walk in confident and ready.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="disclaimer mb-5">
              <p className="text-sm text-amber-800">
                ⚠️ I'm a coaching tool, not a lawyer. I provide legal education and practical preparation guidance. For legal advice, consult a licensed family law attorney.
              </p>
            </div>

            {/* Quick-start prompts */}
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Get started</p>
            <div className="grid grid-cols-1 gap-2">
              {QUICK_PROMPTS.map(p => (
                <button
                  key={p.label}
                  onClick={() => sendMessage(p.text)}
                  className="text-left px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors"
                >
                  <span className="text-base font-medium text-slate-700">{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <Bubble key={i} msg={msg} />
        ))}

        {/* Typing indicator */}
        {loading && <TypingIndicator />}

        {/* Error */}
        {error && (
          <p className="text-center text-sm text-red-500 mt-2">{error}</p>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input bar ── */}
      <div className="border-t border-slate-100 px-4 py-3 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask your coach anything…"
            disabled={loading}
            className="flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-200 disabled:opacity-50 transition-colors"
            style={{ minHeight: 50, maxHeight: 120 }}
          />
          <VoiceAgent
            agent="coach"
            onTranscript={(text, role) => {
              setMessages(m => [...m, { role: role === 'user' ? 'user' : 'ai', text, time: new Date() }])
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="w-11 h-11 flex-shrink-0 rounded-full bg-brand-700 text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-all"
            aria-label="Send message"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          </button>
        </div>
        <p className="text-center text-sm text-slate-500 mt-2">
          Legal education only — not legal advice
        </p>
      </div>
    </div>
  )
}
